export interface PageTitle {
    /** Route prefix this title applies to (longest match wins). */
    prefix: string;
    title: string;
    subtitle?: string;
}

/**
 * Single source of truth for the per-page subtitle. It is no longer rendered
 * in the content header — it now surfaces as a hover flyout on the matching
 * AppSidebar nav item (see AppSidebar.tsx). `title` is retained for
 * identification/tests but is not rendered anywhere.
 */
export const PAGE_TITLES: PageTitle[] = [
    { prefix: '/clusters', title: 'Clusters', subtitle: 'Explore your URL universe — clusters, trends, and briefs.' },
    // deepcontext-736j: My Briefs gets its own subtitle (longest-prefix wins
    // over the generic /clusters entry above) — briefs are company-wide, not
    // part of the "URL universe" market-scoped chrome the sibling subroutes share.
    { prefix: '/clusters/briefs', title: 'My Briefs', subtitle: 'Saved brand briefs shared across your company.' },
    { prefix: '/prompts', title: 'Brand Blueprints', subtitle: 'Manage your custom brand blueprints and rules' },
    { prefix: '/media-planning', title: 'Media Planning', subtitle: 'Explore available inventory and get AI-powered insights for your campaigns' },
    { prefix: '/relevance-engine', title: 'Relevance Engine', subtitle: 'Score URLs against a Brand Blueprint to find inventory that matches your campaign intent.' },
    { prefix: '/insights', title: 'Analysis Overview', subtitle: 'Analysis statistics and trends' },
    { prefix: '/history', title: 'Analysis Audit', subtitle: 'Review and audit the history of past URL analyses.' },
    { prefix: '/site-lists', title: 'Site Lists', subtitle: 'Manage domain lists for filtering scheduled job URLs' },
    { prefix: '/explore', title: 'Explore', subtitle: 'Describe your targeting needs in plain language to discover matching content and inventory.' },
    { prefix: '/admin/taxonomy', title: 'Taxonomy', subtitle: 'Export the Equativ taxonomy as a CSV file.' },
    { prefix: '/url-lookup', title: 'URL Lookup', subtitle: 'Look up the stored analysis and classification for a specific URL.' },
    { prefix: '/scheduled-jobs', title: 'Scheduled Jobs', subtitle: 'Create and manage recurring URL scanning jobs.' },
    { prefix: '/admin/queue', title: 'Queue Admin', subtitle: 'Monitor and manage the analysis queue.' },
    { prefix: '/admin/audit', title: 'Audit', subtitle: 'Who created, changed or deleted what.' },
    { prefix: '/admin/users', title: 'Manage Users', subtitle: 'Manage users and companies, their roles, and access.' },
];

/**
 * Resolve the title for a pathname by longest matching prefix, so e.g.
 * `/clusters/briefs` resolves to the `/clusters` entry. Returns `null` when no
 * entry matches (e.g. admin routes that keep their in-page heading).
 */
export function resolvePageTitle(pathname: string): PageTitle | null {
    const matches = PAGE_TITLES.filter((p) => pathname.startsWith(p.prefix));
    if (matches.length === 0) return null;
    return matches.sort((a, b) => b.prefix.length - a.prefix.length)[0];
}
